import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Checkbox, Modal, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { pwr5c4Api, ReverseCandidate } from '../../services/pwr5c4Api';
import './PWR5C4Form.css';

// ---- Item state with selection ----
interface ItemState {
  item: ReverseCandidate;
  selected: boolean;
}

// ---- Component ----
const PWR5C4Form: React.FC = () => {
  const navigate = useNavigate();
  const [startNo, setStartNo] = useState('');
  const [endNo, setEndNo] = useState('');
  const [items, setItems] = useState<ItemState[]>([]);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [queried, setQueried] = useState(false);

  // ---- Derived state ----
  const selectedCount = items.filter((s) => s.selected).length;
  const allSelected = items.length > 0 && items.every((s) => s.selected);
  const someSelected = items.some((s) => s.selected) && !allSelected;

  // ---- Query ----
  const onQuery = useCallback(async () => {
    setLoading(true);
    setQueried(false);
    try {
      const res = await pwr5c4Api.queryCandidates(startNo, endNo);
      const mapped: ItemState[] = (res.items || []).map((item) => ({
        item,
        selected: false,
      }));
      setItems(mapped);
      setQueried(true);
      if (mapped.length === 0) {
        message.info('查無符合條件的交運單');
      } else {
        message.success(`查詢到 ${mapped.length} 筆資料`);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '查詢失敗');
    } finally {
      setLoading(false);
    }
  }, [startNo, endNo]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') onQuery();
    },
    [onQuery]
  );

  // ---- Toggle selection ----
  const handleToggle = useCallback((no: string, checked: boolean) => {
    setItems((prev) =>
      prev.map((s) =>
        s.item.fc120_01 === no ? { ...s, selected: checked } : s
      )
    );
  }, []);

  const handleSelectAll = useCallback((checked: boolean) => {
    setItems((prev) => prev.map((s) => ({ ...s, selected: checked })));
  }, []);

  // ---- Execute reversal ----
  const onExecute = useCallback(() => {
    if (selectedCount === 0) {
      message.warning('請先勾選要沖銷的交運單');
      return;
    }
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷已勾選的 ${selectedCount} 筆交運單？此操作不可復原。`,
      okText: '確定沖銷',
      okType: 'primary',
      cancelText: '取消',
      onOk: async () => {
        const selectedNos = items
          .filter((s) => s.selected)
          .map((s) => s.item.fc120_01);
        setExecuting(true);
        try {
          const res = await pwr5c4Api.reverse(selectedNos);
          if (res.failed === 0) {
            message.success(res.message || `沖銷完成，成功 ${res.success} 筆`);
          } else {
            message.warning(
              res.message || `成功 ${res.success} 筆，失敗 ${res.failed} 筆`
            );
            if (res.errors && res.errors.length > 0) {
              res.errors.forEach((err) => message.error(err));
            }
          }
          setItems([]);
          setQueried(false);
        } catch (e: unknown) {
          const err = e as { response?: { data?: { error?: string } } };
          message.error(err.response?.data?.error || '沖銷執行失敗');
        } finally {
          setExecuting(false);
        }
      },
    });
  }, [selectedCount, items]);

  // ---- Table columns ----
  const columns: ColumnsType<ItemState> = [
    {
      title: (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      dataIndex: 'selected',
      key: 'selected',
      width: 50,
      align: 'center',
      render: (_: unknown, record: ItemState) => (
        <Checkbox
          checked={record.selected}
          onChange={(e) => handleToggle(record.item.fc120_01, e.target.checked)}
        />
      ),
    },
    {
      title: '交運單號',
      dataIndex: ['item', 'fc120_01'],
      key: 'fc120_01',
      width: 110,
    },
    {
      title: '交運日期',
      dataIndex: ['item', 'fc120_11'],
      key: 'fc120_11',
      width: 110,
    },
    {
      title: '客戶代碼',
      dataIndex: ['item', 'fc120_12'],
      key: 'fc120_12',
      width: 110,
    },
    {
      title: '交運類別',
      dataIndex: ['item', 'fc120_13'],
      key: 'fc120_13',
      width: 90,
    },
    {
      title: '部門代碼',
      dataIndex: ['item', 'fc120_17'],
      key: 'fc120_17',
      width: 90,
    },
    {
      title: '幣別',
      dataIndex: ['item', 'fc120_18'],
      key: 'fc120_18',
      width: 70,
    },
    {
      title: '匯率',
      dataIndex: ['item', 'fc120_19'],
      key: 'fc120_19',
      width: 80,
      align: 'right',
    },
    {
      title: '金額',
      dataIndex: ['item', 'fc120_20'],
      key: 'fc120_20',
      width: 110,
      align: 'right',
    },
    {
      title: '狀態碼',
      dataIndex: ['item', 'fc120_80'],
      key: 'fc120_80',
      width: 80,
    },
    {
      title: '沖銷次數',
      dataIndex: ['item', 'fc120_90'],
      key: 'fc120_90',
      width: 80,
      align: 'right',
    },
  ];

  // ---- Render ----
  return (
    <div className="pwr5c4-form">
      {(loading || executing) && (
        <div className="loading-overlay">
          {executing ? '沖銷執行中...' : '載入中...'}
        </div>
      )}

      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                首頁
              </a>
              <span>/</span>
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                交運管理
              </a>
              <span>/</span>
              <span className="current">交運單沖銷核准</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>交運單沖銷核准</h1>
            <span className="badge">PWR5C4</span>
          </div>
        </div>

        {/* Query Bar */}
        <div className="query-bar">
          <label>交運單號 .從:</label>
          <Input
            value={startNo}
            onChange={(e) => setStartNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="起始單號"
            style={{ width: 160 }}
            allowClear
          />
          <span className="separator">至</span>
          <Input
            value={endNo}
            onChange={(e) => setEndNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="結束單號"
            style={{ width: 160 }}
            allowClear
          />
          <Button type="primary" onClick={onQuery} loading={loading}>
            確定範圍
          </Button>
        </div>

        {/* Table Card */}
        <div className="table-card">
          {queried && (
            <div className="table-header">
              <div className="info">
                共 <strong>{items.length}</strong> 筆
                {selectedCount > 0 && (
                  <span>
                    ，已勾選 <strong>{selectedCount}</strong> 筆
                  </span>
                )}
              </div>
            </div>
          )}

          {queried ? (
            <Table<ItemState>
              columns={columns}
              dataSource={items}
              rowKey={(record) => record.item.fc120_01}
              pagination={false}
              size="small"
              scroll={{ y: 420 }}
              locale={{ emptyText: '查無資料' }}
            />
          ) : (
            <div className="empty-state">
              請輸入交運單號範圍並點擊「確定範圍」查詢
            </div>
          )}
        </div>

        {/* Bottom Action Bar */}
        <div className="action-bar">
          <Button
            type="primary"
            size="large"
            disabled={selectedCount === 0 || executing}
            loading={executing}
            onClick={onExecute}
          >
            存檔 ({selectedCount})
          </Button>
          <Button
            danger
            size="large"
            onClick={() => navigate('/erp')}
          >
            離開
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PWR5C4Form;
